import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import { useNavigate } from 'react-router-dom';

const STATUS_COLORS = {
  available: 'bg-green-400',
  on_job: 'bg-amber-400',
  driving: 'bg-blue-400',
  break: 'bg-orange-400',
  offline: 'bg-gray-400',
};

const TeamOverviewWidget = ({ orgId }) => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!orgId) return;
    const fetch = async () => {
      setLoading(true);
      try {
        const now = new Date();
        const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)?.toISOString();

        const [profilesRes, jobsRes] = await Promise.all([
          supabase?.from('profiles')?.select('id, full_name, email, role, extended_role')?.eq('org_id', orgId)?.limit(8),
          supabase?.from('jobs')?.select('assigned_to, status')?.eq('org_id', orgId)?.is('deleted_at', null)?.gte('created_at', firstOfMonth),
        ]);

        const profiles = profilesRes?.data || [];
        const jobs = jobsRes?.data || [];

        const jobCountMap = {};
        const completedMap = {};
        jobs?.forEach(j => {
          if (!j?.assigned_to) return;
          jobCountMap[j.assigned_to] = (jobCountMap?.[j?.assigned_to] || 0) + 1;
          if (j?.status === 'completed') completedMap[j.assigned_to] = (completedMap?.[j?.assigned_to] || 0) + 1;
        });

        const enriched = profiles?.map(p => ({
          ...p,
          jobsThisMonth: jobCountMap?.[p?.id] || 0,
          completedThisMonth: completedMap?.[p?.id] || 0,
          initials: (p?.full_name || p?.email || '?')?.split(' ')?.map(w => w?.[0])?.join('')?.toUpperCase()?.slice(0, 2),
        }))?.sort((a, b) => b?.jobsThisMonth - a?.jobsThisMonth);

        setMembers(enriched);
      } catch (err) {
        console.error('Team overview error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [orgId]);

  const AVATAR_COLORS = ['bg-blue-500', 'bg-purple-500', 'bg-green-500', 'bg-amber-500', 'bg-pink-500', 'bg-teal-500', 'bg-red-500', 'bg-indigo-500'];

  return (
    <motion.div
      className="bg-card border border-border rounded-2xl p-5 shadow-sm"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.3 }}
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base font-semibold text-foreground">Team This Month</h3>
          <p className="text-xs text-muted-foreground mt-0.5">{members?.length} members</p>
        </div>
        <button onClick={() => navigate('/settings')} className="text-xs text-primary hover:underline font-medium">Manage →</button>
      </div>
      {loading ? (
        <div className="space-y-3">
          {[1,2,3]?.map(i => <div key={i} className="h-12 bg-muted animate-pulse rounded-xl" />)}
        </div>
      ) : members?.length === 0 ? (
        <div className="text-center py-6">
          <Icon name="Users" size={28} className="text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">No team members</p>
        </div>
      ) : (
        <div className="space-y-2">
          {members?.slice(0, 5)?.map((m, i) => (
            <div key={m?.id} className="flex items-center gap-3 p-2 rounded-xl hover:bg-muted/40 transition-colors">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 ${AVATAR_COLORS?.[i % AVATAR_COLORS?.length]}`}>
                {m?.initials}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-foreground truncate">{m?.full_name || m?.email}</p>
                <p className="text-xs text-muted-foreground capitalize">{m?.extended_role || m?.role || 'Member'}</p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-xs font-semibold text-foreground">{m?.jobsThisMonth}</p>
                <p className="text-xs text-muted-foreground">jobs</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
};

export default TeamOverviewWidget;
